import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ArrowRight, ArrowLeft, Play, SkipForward } from 'lucide-react';
import { CanvasWidget } from '../canvas/types';
import { 
  createWalletManagerWidget,
  createAdvancedChartsWidget,
  createTradesBuySellWidget,
  createOrderBookWidget,
  createSniperBotWidget,
  createAdvancedAutoTraderWidget,
  createCopyTraderWidget
} from '../../widgets';

interface OnboardingGuideProps {
  isOpen: boolean;
  onClose: () => void;
  onSkipAll: () => void;
  canvasRef: React.RefObject<{ addWidget: (widget: Partial<CanvasWidget>) => void }>;
}

interface GuideStep {
  id: string;
  title: string;
  description: string;
  targetSelector?: string;
  targetElement?: 'canvas' | 'sidebar' | 'navbar' | 'templates' | 'walletconnection';
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  highlightBounds?: { x: number; y: number; width: number; height: number };
  action?: () => void;
  skipStep?: boolean;
  delayHighlight?: number; // Add delay for highlight after action
}

const OnboardingGuide: React.FC<OnboardingGuideProps> = ({
  isOpen,
  onClose,
  onSkipAll,
  canvasRef
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [guideBounds, setGuideBounds] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const steps: GuideStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to EKKO V2 BETA! 🚀',
      description: 'Get ready to master the most advanced trading platform. This quick guide will show you how to navigate and use all the powerful features.',
      position: 'center'
    },
    {
      id: 'canvas-intro',
      title: 'Canvas Workspace',
      description: 'This is your trading canvas! Right-click anywhere to add widgets, drag widgets around, and use your mouse wheel to zoom in and out. Let\'s add a Wallet Manager widget to get started.',
      targetElement: 'canvas',
      position: 'center',
      action: () => {
        // Add WalletManager widget to demonstrate
        if (canvasRef.current) {
          const walletWidget = createWalletManagerWidget();
          canvasRef.current.addWidget({
            ...walletWidget,
            position: { x: 100, y: 100 },
            size: { width: 400, height: 500 }
          });
        }
      },
      delayHighlight: 500 // Delay to allow widget creation
    },
    {
      id: 'canvas-templates',
      title: 'Canvas Templates - Quick Start',
      description: 'For faster setup, use Canvas Templates! Click the templates button in the bottom toolbar to instantly load pre-configured widget layouts for different trading strategies.',
      targetSelector: '[data-onboarding="templates-button"]',
      position: 'top'
    },
    {
      id: 'wallet-manager',
      title: 'Wallet Manager - Your Trading Hub',
      description: 'The Wallet Manager is essential for trading. Here you can create new trading wallets, backup your private keys securely, and transfer funds between your main wallet and trading wallets.',
      targetSelector: '.widget[data-widget-type="wallet-manager"]',
      position: 'right',
      skipStep: true // Skip if no wallet widget is found
    },
    {
      id: 'wallet-creation',
      title: 'Create & Fund Trading Wallets',
      description: 'Always create separate trading wallets for security! Create a trading wallet, backup your private key safely, then transfer SOL from your main wallet. Once funded, activate it in the wallet connection dropdown.',
      targetSelector: '.widget[data-widget-type="wallet-manager"]',
      position: 'right',
      skipStep: true
    },
    {
      id: 'advanced-charts',
      title: 'Advanced Charts',
      description: 'Time to add charts! Right-click the canvas and add an Advanced Charts widget. This provides professional TradingView charts with all technical indicators.',
      targetElement: 'canvas',
      position: 'center',
      action: () => {
        // Add Advanced Charts widget
        if (canvasRef.current) {
          const chartWidget = createAdvancedChartsWidget();
          canvasRef.current.addWidget({
            ...chartWidget,
            position: { x: 550, y: 100 },
            size: { width: 800, height: 500 }
          });
        }
      },
      delayHighlight: 500
    },
    {
      id: 'buy-sell-widget',
      title: 'Buy/Sell Widget',
      description: 'Add the Buy/Sell widget for quick trading! This widget lets you execute trades directly from the canvas with customizable slippage and MEV protection.',
      targetElement: 'canvas',
      position: 'center',
      action: () => {
        // Add Buy/Sell widget
        if (canvasRef.current) {
          const buySellWidget = createTradesBuySellWidget();
          canvasRef.current.addWidget({
            ...buySellWidget,
            position: { x: 1400, y: 100 },
            size: { width: 350, height: 600 }
          });
        }
      },
      delayHighlight: 500
    },
    {
      id: 'order-book',
      title: 'Order Book',
      description: 'Add an Order Book widget to see real-time market depth and liquidity. This helps you understand the market before placing trades.',
      targetElement: 'canvas',
      position: 'center',
      action: () => {
        // Add Order Book widget
        if (canvasRef.current) {
          const orderBookWidget = createOrderBookWidget();
          canvasRef.current.addWidget({
            ...orderBookWidget,
            position: { x: 1800, y: 100 },
            size: { width: 300, height: 400 }
          });
        }
      },
      delayHighlight: 500
    },
    {
      id: 'token-feeds',
      title: 'Token Feeds - Discover New Opportunities',
      description: 'The sidebar shows live token feeds with new, trending, and migrated tokens. Click on any token card to instantly update all your widgets with that token\'s data!',
      targetElement: 'sidebar',
      position: 'right'
    },
    {
      id: 'navbar-search',
      title: 'Navbar Search - Find Any Token',
      description: 'Use the search bar in the navbar to find specific tokens quickly. Just like the token feeds, clicking any search result will update all your active widgets!',
      targetSelector: '[data-onboarding="navbar-search-button"]',
      position: 'bottom'
    },
    {
      id: 'automated-bots',
      title: 'Automated Trading Bots',
      description: 'EKKO features powerful automated trading bots: Sniper Bot for catching new token launches, Auto Trader for algorithmic strategies, and Copy Trader to follow successful traders.',
      targetElement: 'canvas',
      position: 'center',
      action: () => {
        // Add a few bot widgets
        if (canvasRef.current) {
          const sniperBot = createSniperBotWidget(() => {});
          const autoTrader = createAdvancedAutoTraderWidget();
          const copyTrader = createCopyTraderWidget();
          
          canvasRef.current.addWidget({
            ...sniperBot,
            position: { x: 100, y: 650 },
            size: { width: 400, height: 400 }
          });
          canvasRef.current.addWidget({
            ...autoTrader,
            position: { x: 550, y: 650 },
            size: { width: 400, height: 400 }
          });
          canvasRef.current.addWidget({
            ...copyTrader,
            position: { x: 1000, y: 650 },
            size: { width: 400, height: 400 }
          });
        }
      },
      delayHighlight: 800 // Longer delay for multiple widgets
    },
    {
      id: 'completion',
      title: 'Ready to Trade! 🎉',
      description: 'You\'re all set to explore EKKO V2 BETA! Join our community to help us improve the platform, report bugs, and share your trading success. Happy trading!',
      position: 'center'
    }
  ];

  // Set canvas zoom to 70% when onboarding starts
  useEffect(() => {
    if (isOpen) {
      // Dispatch zoom change event to Canvas component
      console.log('🎯 Onboarding: Setting canvas zoom to 70%');
      window.dispatchEvent(new CustomEvent('ekko:onboarding_zoom', {
        detail: { zoom: 0.7 }
      }));
    } else {
      // Restore zoom when onboarding closes
      console.log('🎯 Onboarding: Restoring canvas zoom');
      window.dispatchEvent(new CustomEvent('ekko:onboarding_zoom_restore'));
    }
  }, [isOpen]);

  const getCurrentStepData = () => steps[currentStep];

  const handleNext = () => {
    const currentStepData = getCurrentStepData();
    
    // Execute step action if present
    if (currentStepData.action) {
      currentStepData.action();
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const updateHighlightBounds = useCallback(() => {
    const currentStepData = getCurrentStepData();
    
    if (!currentStepData.targetSelector && !currentStepData.targetElement) {
      setGuideBounds(null);
      return;
    }

    let targetElement: Element | null = null;

    if (currentStepData.targetSelector) {
      targetElement = document.querySelector(currentStepData.targetSelector);
    } else if (currentStepData.targetElement) {
      switch (currentStepData.targetElement) {
        case 'canvas':
          targetElement = document.querySelector('[data-onboarding="canvas"]') || 
                         document.querySelector('.canvas-container') ||
                         document.querySelector('div[style*="transform: scale"]')?.parentElement;
          break;
        case 'sidebar':
          targetElement = document.querySelector('[data-onboarding="sidebar"]') ||
                         document.querySelector('.backdrop-blur-sm');
          break;
        case 'navbar':
          targetElement = document.querySelector('[data-onboarding="navbar"]') ||
                         document.querySelector('nav');
          break;
        case 'templates':
          targetElement = document.querySelector('[data-onboarding="templates-button"]');
          break;
        case 'walletconnection':
          targetElement = document.querySelector('[data-onboarding="wallet-connection"]');
          break;
      }
    }

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setGuideBounds({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height
      });
      console.log(`🎯 Onboarding: Highlighting element for step ${currentStepData.id}`, {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height
      });
    } else {
      // If target not found and step should be skipped, skip it
      if (currentStepData.skipStep) {
        console.log(`🎯 Onboarding: Skipping step ${currentStepData.id} - target element not found`);
        setTimeout(() => {
          if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
          }
        }, 100);
      } else {
        console.log(`🎯 Onboarding: Target element not found for step ${currentStepData.id}`);
      }
      setGuideBounds(null);
    }
  }, [currentStep]);

  useEffect(() => {
    if (isOpen) {
      const currentStepData = getCurrentStepData();
      
      // If step has a delay for highlighting, wait before updating bounds
      const delay = currentStepData.delayHighlight || 100;
      
      const timer = setTimeout(() => {
        updateHighlightBounds();
      }, delay);
      
      // Update bounds on window resize
      const handleResize = () => {
        setTimeout(updateHighlightBounds, delay);
      };
      window.addEventListener('resize', handleResize);
      
      // Update bounds when DOM changes (widgets added, etc.)
      const observer = new MutationObserver(() => {
        setTimeout(updateHighlightBounds, delay);
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', handleResize);
        observer.disconnect();
      };
    }
  }, [isOpen, currentStep, updateHighlightBounds]);

  if (!isOpen) return null;

  const currentStepData = getCurrentStepData();
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div 
      ref={overlayRef}
      className="fixed inset-0 z-[100] pointer-events-none"
      style={{ 
        background: 'transparent'
      }}
    >
      {/* Highlight area */}
      {guideBounds && (
        <div
          className="absolute pointer-events-none border-2 border-[#24c35b] rounded-lg shadow-[0_0_20px_rgba(36,195,91,0.3)]"
          style={{
            left: guideBounds.x - 4,
            top: guideBounds.y - 4,
            width: guideBounds.width + 8,
            height: guideBounds.height + 8,
            animation: 'pulse 2s infinite'
          }}
        />
      )}

      {/* Guide popup - Positioned on the right side */}
      <div className="absolute inset-0 flex items-center justify-end pr-6 pointer-events-none">
        <div 
          className="pointer-events-auto bg-white border border-gray-200/50 rounded-xl shadow-2xl w-96 overflow-hidden"
          style={{
            backdropFilter: "blur(12px)",
            boxShadow: "0 8px 32px rgba(36, 195, 91, 0.1)"
          }}
        >
          {/* Header */}
          <div className="p-4 border-b border-gray-200/50 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#24c35b]"></div>
                <h3 className="text-lg font-bold text-gray-900">EKKO Guide</h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-900 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Progress bar */}
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Step {currentStep + 1} of {steps.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div 
                  className="bg-[#24c35b] h-1 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              {currentStepData.title}
            </h4>
            <p className="text-gray-600 leading-relaxed mb-6">
              {currentStepData.description}
            </p>

            {/* Navigation buttons */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  {currentStep > 0 && (
                    <button
                      onClick={handlePrevious}
                      className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Previous
                    </button>
                  )}
                  <button
                    onClick={handleSkip}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    <SkipForward className="w-4 h-4" />
                    Skip
                  </button>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={onSkipAll}
                    className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    Skip All
                  </button>
                  <button
                    onClick={handleNext}
                    className="flex items-center gap-2 px-4 py-2 bg-[#24c35b] text-white text-sm font-medium rounded-lg hover:bg-[#1ea348] transition-colors"
                  >
                    {currentStep < steps.length - 1 ? (
                      <>
                        Next
                        <ArrowRight className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        Finish
                        <Play className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for pulse animation */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 20px rgba(36, 195, 91, 0.3);
          }
          50% {
            box-shadow: 0 0 30px rgba(36, 195, 91, 0.6);
          }
        }
      `}</style>
    </div>
  );
};

export default OnboardingGuide; 